import React from 'react';
import styled from 'styled-components';
import Hero from '../components/Hero';
import ServiceDetail from '../components/ServiceDetail';
import ContactForm from '../components/ContactForm';
import SectionTitle from '../components/SectionTitle';
import SEO from '../components/SEO';
import { audioSolutionsImage, audioSolutionsDetailImage } from '../assets/images';
import { 
  FaHeadphones, 
  FaTools, 
  FaVolumeUp, 
  FaMicrophone, 
  FaWrench,
  FaChalkboardTeacher,
  FaBuilding, 
  FaBriefcase, 
  FaHome 
} from 'react-icons/fa';

const KeenAcousticSciences = () => {
  const features = [
    'Live sound engineering for events of all sizes',
    'Custom nightclub sound system installation & optimization',
    'Professional audio equipment repair & maintenance',
    'Speaker system tuning & calibration',
    'Custom-crafted speaker fabrication',
    'Audio consultation services',
    'Multi-zone audio systems',
    'Acoustic treatment & sound optimization',
    'Audio-visual integration',
    'Ongoing maintenance & support'
  ];

  return (
    <main>
      <SEO 
        title="Keen Acoustic Sciences | TK DJ Services"
        description="Professional audio engineering, installation and custom solutions for venues, businesses, and events. Expert sound system design, integration, and equipment services."
        url="/keen-acoustic-sciences"
        canonical="/keen-acoustic-sciences"
        image="/images/AdobeStock_364280228.jpeg"
      />
      <Hero 
        backgroundImage={audioSolutionsImage}
        title="Keen Acoustic Sciences"
        subtitle="Expert audio engineering, installation, and equipment services for venues, businesses, and events."
        buttonText="Learn More"
        buttonLink="#comprehensive-solutions"
      />
      
      <ServiceDetailSection id="comprehensive-solutions">
        <div className="container">
          <ServiceDetail 
            title="Comprehensive Audio Solutions & Installation"
            description="Keen Acoustic Sciences, a division of TK DJ Services, offers comprehensive audio engineering and installation solutions for venues, events, and audio professionals. With 15+ years of experience as an electronics repair technician and audio engineer, we provide expert services ranging from live sound engineering to custom sound system design and implementation. Our technical expertise ensures optimal sound quality and reliable performance for any audio application."
            features={features}
            image={audioSolutionsDetailImage}
            ctaText="Contact Us"
            ctaLink="#contact"
          />
        </div>
      </ServiceDetailSection>
      
      <ServicesSection id="services">
        <div className="container">
          <SectionTitle 
            title="Our Audio Engineering Services" 
            subtitle="We offer a comprehensive range of audio services to meet the needs of venues, events, and audio professionals."
            center
          />
          
          <ServiceGrid>
            <ServiceCard>
              <ServiceIcon>
                <FaVolumeUp />
              </ServiceIcon>
              <ServiceTitle>Live Sound Engineering</ServiceTitle>
              <ServiceDescription>
                Professional live sound engineering for concerts, events, and performances. We ensure optimal sound quality, balanced levels, and clear audio for performers and audience alike. Our expertise covers FOH (front of house) and monitor mixing for events of all sizes.
              </ServiceDescription>
            </ServiceCard>
            
            <ServiceCard>
              <ServiceIcon>
                <FaHeadphones />
              </ServiceIcon>
              <ServiceTitle>Custom Sound System Installation</ServiceTitle>
              <ServiceDescription>
                Specialized in nightclub and venue sound system installation, we design and implement audio solutions tailored to your space's acoustics and requirements. From speaker placement to system integration, we create immersive audio environments.
              </ServiceDescription>
            </ServiceCard>
            
            <ServiceCard>
              <ServiceIcon>
                <FaTools />
              </ServiceIcon>
              <ServiceTitle>Equipment Repair & Maintenance</ServiceTitle>
              <ServiceDescription>
                With extensive experience in electronics repair, we offer professional servicing of audio equipment including amplifiers, mixers, speakers, and DJ gear. Regular maintenance plans available to keep your equipment in optimal condition.
              </ServiceDescription>
            </ServiceCard>
            
            <ServiceCard>
              <ServiceIcon>
                <FaMicrophone />
              </ServiceIcon>
              <ServiceTitle>Speaker Tuning & Calibration</ServiceTitle>
              <ServiceDescription>
                Precise speaker system tuning and calibration services to optimize sound quality in any environment. Using professional acoustic analysis tools, we ensure your system delivers balanced, clear audio throughout your venue.
              </ServiceDescription>
            </ServiceCard>
            
            <ServiceCard>
              <ServiceIcon>
                <FaWrench />
              </ServiceIcon>
              <ServiceTitle>Custom Speaker Fabrication</ServiceTitle>
              <ServiceDescription>
                Specializing in custom-crafted speaker solutions, we design and build speaker systems tailored to specific requirements. From subwoofers to full-range cabinets, our custom fabrication delivers exceptional sound quality and durability.
              </ServiceDescription>
            </ServiceCard>
            
            <ServiceCard>
              <ServiceIcon>
                <FaChalkboardTeacher />
              </ServiceIcon>
              <ServiceTitle>Audio Consultation & Training</ServiceTitle>
              <ServiceDescription>
                Professional consultation services for venues, studios, and audio enthusiasts. We provide expert advice on system design, equipment selection, acoustic treatment, and workflow optimization. Training sessions available for staff on equipment operation and audio fundamentals.
              </ServiceDescription>
            </ServiceCard>
          </ServiceGrid>
        </div>
      </ServicesSection>
      
      <SolutionsSection id="solutions">
        <div className="container">
          <SectionTitle 
            title="Our Installation Solutions" 
            subtitle="We provide tailored audio installation services for various environments, each with unique requirements and considerations."
            center
          />
          
          <SolutionGrid>
            <SolutionCard>
              <SolutionIcon>
                <FaBuilding />
              </SolutionIcon>
              <SolutionTitle>Nightclub & Venue Systems</SolutionTitle>
              <SolutionDescription>
                Specialized in high-impact sound systems for nightclubs, music venues, and performance spaces. We design and install powerful, clear audio systems that deliver immersive experiences with proper coverage, powerful bass response, and crystal-clear highs. Our nightclub installations include DJ booth integration, dance floor coverage, and VIP area audio zoning.
              </SolutionDescription>
            </SolutionCard>
            
            <SolutionCard>
              <SolutionIcon>
                <FaMicrophone />
              </SolutionIcon>
              <SolutionTitle>Bar & Restaurant Audio</SolutionTitle>
              <SolutionDescription>
                Custom audio solutions for bars, restaurants, and hospitality venues that enhance the dining experience without overwhelming conversation. We implement multi-zone systems with independent volume control, background music integration, and optional microphone systems for announcements or small performances.
              </SolutionDescription>
            </SolutionCard>
            
            <SolutionCard>
              <SolutionIcon>
                <FaBriefcase />
              </SolutionIcon>
              <SolutionTitle>Corporate & Commercial</SolutionTitle>
              <SolutionDescription>
                Professional audio systems for corporate environments, retail spaces, and commercial facilities. From conference room sound systems and presentation audio to background music for retail environments, we design and install solutions that enhance your business operations and customer experience.
              </SolutionDescription>
            </SolutionCard>
            
            <SolutionCard>
              <SolutionIcon>
                <FaHome />
              </SolutionIcon>
              <SolutionTitle>Home & Studio Audio</SolutionTitle>
              <SolutionDescription>
                Audio consultation and setup services for home theaters, listening rooms, and recording studios. We provide expert guidance on equipment selection, speaker placement, and system calibration. While we don't perform construction work, we can recommend qualified contractors for any necessary structural modifications. Our focus is on delivering exceptional sound quality through proper equipment selection and setup.
              </SolutionDescription>
            </SolutionCard>
          </SolutionGrid>
        </div>
      </SolutionsSection>
      
      <ProcessSection>
        <div className="container">
          <SectionTitle 
            title="Our Installation Process" 
            subtitle="We follow a comprehensive process to ensure your audio installation meets your expectations and delivers exceptional sound quality."
            center
          />
          
          <ProcessTimeline>
            <TimelineItem>
              <TimelineNumber>1</TimelineNumber>
              <TimelineContent>
                <TimelineTitle>Consultation & Assessment</TimelineTitle>
                <TimelineDescription>
                  We begin with a thorough consultation to understand your needs, preferences, and budget. Our team assesses your space, taking measurements and evaluating acoustic properties to inform our system design.
                </TimelineDescription>
              </TimelineContent>
            </TimelineItem>
            
            <TimelineItem>
              <TimelineNumber>2</TimelineNumber>
              <TimelineContent>
                <TimelineTitle>System Design & Proposal</TimelineTitle>
                <TimelineDescription>
                  Based on our assessment, we create a detailed system design including equipment selection, speaker placement, wiring plans, and control interfaces. We present a comprehensive proposal with clear pricing and implementation timeline.
                </TimelineDescription>
              </TimelineContent>
            </TimelineItem>
            
            <TimelineItem>
              <TimelineNumber>3</TimelineNumber>
              <TimelineContent>
                <TimelineTitle>Professional Installation</TimelineTitle>
                <TimelineDescription>
                  Our experienced technicians handle the installation with minimal disruption to your space. We carefully install all components, run wiring neatly and discreetly, and ensure all connections are secure and properly terminated.
                </TimelineDescription>
              </TimelineContent>
            </TimelineItem>
            
            <TimelineItem>
              <TimelineNumber>4</TimelineNumber>
              <TimelineContent>
                <TimelineTitle>System Calibration & Training</TimelineTitle>
                <TimelineDescription>
                  After installation, we meticulously calibrate your system for optimal performance in your specific space. We provide comprehensive training on system operation and offer ongoing support to ensure you get the most from your audio installation.
                </TimelineDescription>
              </TimelineContent>
            </TimelineItem>
          </ProcessTimeline>
        </div>
      </ProcessSection>
      
      <ContactSection id="contact">
        <div className="container">
          <SectionTitle 
            title="Contact Keen Acoustic Sciences" 
            subtitle="Ready to transform your audio experience? Contact us today to discuss your audio engineering and installation needs."
            center
          />
          <ContactForm />
        </div>
      </ContactSection>
    </main>
  );
};

const ServiceDetailSection = styled.section`
  padding: 8rem 0 5rem;
`;

const ServicesSection = styled.section`
  padding: 5rem 0;
  background-color: rgba(0, 0, 0, 0.3);
`;

const ServiceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
`;

const ServiceCard = styled.div`
  background-color: rgba(19, 19, 19, 0.5);
  border-radius: 8px;
  padding: 2rem;
  transition: transform 0.3s ease;
  height: 100%;
  
  &:hover {
    transform: translateY(-10px);
  }
`;

const ServiceIcon = styled.div`
  font-size: 2.5rem;
  color: var(--color-secondary);
  margin-bottom: 1.5rem;
`;

const ServiceTitle = styled.h3`
  color: var(--color-tertiary);
  margin-bottom: 1rem;
`;

const ServiceDescription = styled.p`
  color: var(--color-tertiary);
  opacity: 0.8;
  line-height: 1.6;
`;

const SolutionsSection = styled.section`
  padding: 5rem 0;
`;

const SolutionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  margin-top: 3rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const SolutionCard = styled.div`
  background-color: rgba(19, 19, 19, 0.5);
  border-radius: 8px;
  padding: 2rem;
  transition: transform 0.3s ease;
  height: 100%;
  
  &:hover {
    transform: translateY(-10px);
  }
`;

const SolutionIcon = styled.div`
  font-size: 2.5rem;
  color: var(--color-secondary);
  margin-bottom: 1.5rem;
`;

const SolutionTitle = styled.h3`
  color: var(--color-tertiary);
  margin-bottom: 1rem;
`;

const SolutionDescription = styled.p`
  color: var(--color-tertiary);
  opacity: 0.8;
  line-height: 1.6;
`;

const ProcessSection = styled.section`
  padding: 5rem 0;
  background-color: rgba(0, 0, 0, 0.3);
`;

const ProcessTimeline = styled.div`
  margin-top: 3rem;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 20px;
    width: 2px;
    background-color: var(--color-secondary);
    opacity: 0.3;
    
    @media (max-width: 768px) {
      left: 15px;
    }
  }
`;

const TimelineItem = styled.div`
  display: flex;
  margin-bottom: 3rem;
  position: relative;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const TimelineNumber = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: var(--color-primary);
  color: var(--color-tertiary);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  margin-right: 2rem;
  z-index: 1;
  
  @media (max-width: 768px) {
    width: 30px;
    height: 30px;
    margin-right: 1rem;
  }
`;

const TimelineContent = styled.div`
  flex: 1;
`;

const TimelineTitle = styled.h3`
  color: var(--color-tertiary);
  margin-bottom: 0.5rem;
`;

const TimelineDescription = styled.p`
  color: var(--color-tertiary);
  opacity: 0.8;
  line-height: 1.6;
`;

const ContactSection = styled.section`
  padding: 5rem 0;
`;

export default KeenAcousticSciences; 